import {
  DataTablePrimeReact,
  PaginationERP,
  TableERP,
} from "@finanedi.es/storybook"
import {
  getContactoPreferenciasById,
  getContactosByNifAndEmpresaId,
} from "api/Contacto.service"
import { GetFacturaProformaByIdDtoProps } from "api/DTOs/GetFacturaProformaByIdDto"
import { GetFacturasProformaByFiltersDtoProps } from "api/DTOs/GetFacturasProformaByFiltersDto"
import {
  FacturaProformaQueryParamsProps,
  deleteFacturaProformaById,
  facturasProformaByFiltersCall,
  getFacturaProforma,
  getFacturaProformaById,
  getFacturaProformaDetalleById,
} from "api/FacturaProforma.service"
import DeleteModal from "common/components/Modal/DeleteModal"
import NoDataTemplate from "common/components/NoDataTemplate"
import { STATIC_ASSETS_URL } from "common/context/constants"
import notification from "common/utils/notification"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import { useFilterMenu } from "hooks/useFilterMenu"
import { useSelector } from "hooks/useSelector"
import { useUsuarioByCurrent } from "hooks/useUsuarioByCurrent"
import { Skeleton } from "primereact/skeleton"
import { useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"
import * as ROUTES from "routes/RoutePaths"
import { globalSelector } from "routes/globalSlice"
import ModalDetail from "../ModalDetail/ModalDetail"
import ModalDetailChildren from "../ModalDetail/ModalDetailChildren"
import { FilterMenu } from "./FilterMenu"
import { FACTURAS_PROFORMA_COLUMNS_ERP } from "./Table.columns"
import {
  PROFORMA_DETAIL_MODAL_ENTRIES,
  proformaToConvertObject,
} from "./constants"

export const Enviadas = () => {
  const { t } = useTranslation()
  const history = useHistory()

  const { usuarioByCurrent } = useUsuarioByCurrent()
  const empresaActiva = usuarioByCurrent?.empresaActiva
  const { formasPago, tiposImpuestosGenerales } = useSelector(globalSelector)

  const [clickedRowData, setClickedRowData] =
    useState<GetFacturaProformaByIdDtoProps>()
  const [sortingCriteria, setSortingCriteria] = useState<
    FacturaProformaQueryParamsProps["SortingCriteria"]
  >({
    sort: null,
    property: undefined,
  })
  const [pagination, setPagination] = useState({
    itemsPerPage: 100,
    skipCount: 0,
  })
  const [showDeleteModal, setShowDeleteModal] = useState<{
    show: boolean
    id: string | undefined
  }>({ show: false, id: undefined })
  const dtRef =
    useRef<DataTablePrimeReact<GetFacturasProformaByFiltersDtoProps[]>>(null)
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)

  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }

  const convertirAfactura = async (id?: string) => {
    try {
      if (!id) {
        // eslint-disable-next-line no-console
        console.error("Error al enviar id de la proforma.")
        return
      }
      const proformaIdToConvert = await getFacturaProformaDetalleById(id)

      const librado = await getContactosByNifAndEmpresaId(
        proformaIdToConvert?.cliente?.empresa.nif,
        empresaActiva?.id
      )

      const dataContactPreferences = await getContactoPreferenciasById(
        librado?.id as string
      )

      const convertToInvoiceObject = proformaToConvertObject({
        id,
        proforma: proformaIdToConvert,
        librado,
        librador: empresaActiva,
        libradoPreferences: dataContactPreferences,
        formasPago,
        tiposImpuestosGenerales,
        t,
      })

      history.push(ROUTES.CREAR_FACTURA, convertToInvoiceObject)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    }
  }

  const fetchItems = useCallback(
    () => (queryParams: FacturaProformaQueryParamsProps) => {
      facturasProformaByFiltersCall?.cancel()
      return getFacturaProforma({ ...queryParams })
    },
    []
  )

  const { items, isLoading, totalCount, setServiceQuery, fetchTableData } =
    useFilterMenu({
      serviceCall: fetchItems,
    })

  const onConfirmDelete = async () => {
    if (showDeleteModal.id) {
      await deleteFacturaProformaById(showDeleteModal.id)
      setShowDeleteModal({ show: false, id: undefined })
      notification.success({
        body: `${t("notification.proformaCorrectyDelete")}`,
      })
      fetchTableData()
    }
  }

  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
  }
  const onRowClick = async ({
    data,
  }: {
    data: GetFacturasProformaByFiltersDtoProps
  }) => {
    const idData = data.id ? await getFacturaProformaById(data.id) : undefined
    setClickedRowData(idData)
  }

  return (
    <>
      <div className='tw-flex tw-flex-row tw-justify-between tw-px-8 tw-pt-6 tw-pb-4 tw-bg-white'>
        <div className='tw-text-xl tw-font-normal tw-flex tw-items-center tw-text-nowrap '>
          {t("nsProformaInvoices.header.title")} (
          {isLoading ? (
            <Skeleton className='!tw-h-5 !tw-w-8 !tw-mx-1' />
          ) : (
            <span>{totalCount}</span>
          )}
          )
        </div>

        <FilterMenu
          maxResultCount={pagination.itemsPerPage}
          sortingCriteria={sortingCriteria}
          resetPagination={handleResetPagination}
          serviceQuery={setServiceQuery}
          skipCount={pagination.skipCount}
        />
      </div>

      <section
        id='contenedor-tabla-facturas-proforma-enviadas'
        className='tw-flex-grow tw-overflow-y-hidden tw-max-h-full tw-w-full tw-h-fit'
      >
        <TableERP
          refTable={dtRef}
          columns={FACTURAS_PROFORMA_COLUMNS_ERP(
            t,
            convertirAfactura,
            setShowDeleteModal
          )}
          className='tw-h-full'
          columnResizeMode='expand'
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={onSort}
          removableSort
          value={items}
          showGridlines
          dataKey='id'
          lazy
          scrollable
          size='small'
          onRowClick={onRowClick}
          loading={isLoading}
          emptyMessage={
            <NoDataTemplate
              icon={`${STATIC_ASSETS_URL}/Imagenes/Ilustraciones/tablas/facturas-de-venta.svg`}
              title='nsProformaInvoices.header.title'
              description='shared.noDataDescription'
              actionLabel='nsProformaInvoices.header.newInvoice'
              showActions={false}
            />
          }
        />
        <PaginationERP
          className={PAGINATION_STYLES}
          totalRecords={totalCount}
          first={pagination.skipCount}
          rows={pagination.itemsPerPage}
          resetScroll={() => dtRef.current && dtRef.current?.resetScroll()}
          setPagination={setPagination}
          optionDropdown={[10, 20, 50, 100]}
          texts={{
            textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
            textCurrentPageReport: `${t("pagination.from")}`,
          }}
        />
      </section>
      <ModalDetail
        visible={Boolean(clickedRowData)}
        onCancel={() => setClickedRowData(undefined)}
        modalTitle={t("nsProformaInvoices.modalDetail.title")}
      >
        <ModalDetailChildren
          ficheroId={clickedRowData?.ficheroId}
          entries={PROFORMA_DETAIL_MODAL_ENTRIES(t, clickedRowData)}
        />
      </ModalDetail>
      <DeleteModal
        visible={showDeleteModal.show}
        onHide={() => setShowDeleteModal({ show: false, id: undefined })}
        size='small'
        iconType='remesas'
        buttonLabel={t("newDoc.facturaProforma.modalDelete.deleteButton")}
        title={t("newDoc.facturaProforma.modalDelete.title")}
        message={t("newDoc.facturaProforma.modalDelete.msg")}
        onConfirm={onConfirmDelete}
      />
    </>
  )
}
